import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchemaV2 } from 'types/pagination';
import { z } from 'zod/v4';

const URL = '/unhandled_debtors';

// #region Variables
const LoadUnhandledClientsVariablesSchema = z.object({});

export type LoadUnhandledClientsVariables = z.infer<typeof LoadUnhandledClientsVariablesSchema>;
// #endregion Variables

// #region Response
const LightUnhandledClientSchema = z.object({
  id: z.coerce.string(),
  averagePaymentDelay: z.coerce.number(),
  remainingBalance: z.coerce.number(),
  fullName: z.string(),
  numberOfTasks: z.number(),
});

const UnhandledClientsSchema = z
  .object({
    unhandledDebtors: z.array(LightUnhandledClientSchema),
    pagination: PaginationSchemaV2,
  })
  .transform(({ unhandledDebtors, pagination }) => ({
    unhandledClients: unhandledDebtors,
    pagination,
  }));

export type UnhandledClients = z.infer<typeof UnhandledClientsSchema>;

const NO_UNHANDLED_CLIENTS: UnhandledClients = {
  unhandledClients: [],
  pagination: { totalItems: 0 },
};
// #endregion Response

// MARK: Load function
export async function loadUnhandledClientsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadUnhandledClientsVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: variables });

  const parseResult = UnhandledClientsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'unhandledClients');
    throw new SilentError();
  }

  return parseResult.data;
}

// MARK: Hook
export function useLoadUnhandledClients(
  variables: LoadUnhandledClientsVariables,
  options: QueryOptions<UnhandledClients> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<UnhandledClients, ApiError>({
    queryKey: ['unhandledClients', variables],
    queryFn: () => loadUnhandledClientsQueryFn(axiosInstance, variables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return NO_UNHANDLED_CLIENTS;
    },
    ...options,
  });

  const namedQueryResult = addResourceNameToQueryResult<
    UnhandledClients,
    ApiError,
    'unhandledClients'
  >('unhandledClients', queryResult);

  return {
    ...namedQueryResult,
    unhandledClients: namedQueryResult.unhandledClients ?? NO_UNHANDLED_CLIENTS,
  };
}
